<template>
  <div class="content bg8">
    <div class="contact-shell">
      <section class="contact-options">
        <div class="option-card featured">
          <div class="eyebrow small">Recommended</div>
          <h2>Book a conversation</h2>
          <p>
            If you already know you want to talk, scheduling a meeting is the simplest way to get started.
          </p>
          <a href="https://calendly.com/foclarity" target="_blank" rel="nofollow" class="inline-link">Go to Calendly</a>
        </div>
        <div class="option-card">
          <div class="eyebrow small">Alternative</div>
          <h2>Send a message</h2>
          <p>
            Use the form below if you want to share context first and have me follow up by email.
          </p>
          <a href="#contact-form" class="inline-link">Jump to the form</a>
        </div>
      </section>

      <section class="contact-hero">
        <div class="contact-copy">
          <div class="eyebrow">Contact</div>
          <h1>Let’s talk about what needs focus.</h1>
          <p class="lead">
            If you are a founder, solopreneur, or small team trying to get more clarity, alignment, and traction,
            I’d be glad to hear what you’re working through.
          </p>
          <p class="sublead">
            The fastest path is to book a conversation. If you prefer, you can also use the contact form below and I’ll follow up.
          </p>
          <div class="hero-actions">
            <a href="https://calendly.com/foclarity" target="_blank" rel="nofollow" class="action-link">
              <button class="action-btn primary">Book a Meeting</button>
            </a>
          </div>
        </div>
        <div class="contact-highlights">
          <div class="highlight-card">
            <strong>Best fit</strong>
            <span>Early founders, owner-operators, and small teams who need more structure around what matters most.</span>
          </div>
          <div class="highlight-card">
            <strong>What we can discuss</strong>
            <span>Priority setting, custom scorecards, team alignment, execution support, and how Foclarity could fit your workflow.</span>
          </div>
          <div class="highlight-card">
            <strong>Prefer email?</strong>
            <span>The hosted form below helps keep spam down while still giving you a direct way to reach out.</span>
          </div>
        </div>
      </section>

      <section id="contact-form" class="form-panel">
        <div class="form-panel-head">
          <div>
            <div class="eyebrow">Contact Form</div>
            <h2>Tell me a little about what you need.</h2>
            <p>
              Share a few details about your business, team, or current challenge and I’ll get back to you.
            </p>
          </div>
        </div>

        <div class="form-frame-wrap">
          <iframe
            class="contact-frame"
            title="Foclarity contact form"
            src="https://us7.list-manage.com/contact-form?u=2c36f327fea570fc06cb3650e&form_id=3ab7a669ccdd4a29b007977401c632fa"
            frameborder="0"
          ></iframe>
        </div>

        <p class="form-note">
          Having trouble with the embedded form?
          <a href="https://us7.list-manage.com/contact-form?u=2c36f327fea570fc06cb3650e&form_id=3ab7a669ccdd4a29b007977401c632fa" target="_blank" rel="nofollow">
            Open it in a new tab
          </a>.
        </p>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  title: 'Contact Foclarity | Book a Founder Focus Call',
  description: 'Contact Foclarity to book a conversation about founder advising, scorecards, team alignment, and practical support for execution.',
  name: 'Contact',
};
</script>

<style lang="scss" scoped>
.contact-shell {
  max-width: 1120px;
  margin: 0 auto;
  padding: 48px 24px 72px;
}

.eyebrow {
  color: #9a6b00;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.eyebrow.small {
  font-size: 0.74rem;
  margin-bottom: 10px;
}

.contact-hero {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 28px;
  align-items: start;
  padding: 18px 0 28px;
  margin-bottom: 12px;
}

.contact-highlights,
.option-card,
.form-panel {
  background: #fff;
  border: 1px solid #dce5ec;
  border-radius: 28px;
  box-shadow: 0 16px 36px rgba(13, 34, 56, 0.05);
}

.contact-copy {
  padding: 18px 0 0;
}

.contact-copy h1 {
  margin: 0 0 18px;
  color: #10263d;
  font-size: clamp(2.4rem, 4vw, 4.2rem);
  line-height: 1.05;
}

.lead {
  font-size: 1.15rem;
  line-height: 1.65;
  color: #29435c;
  margin-bottom: 14px;
}

.sublead {
  color: #5b7084;
  line-height: 1.7;
  margin: 0;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 28px;
}

.action-link {
  text-decoration: none;
}

.action-btn {
  border: none;
  border-radius: 999px;
  padding: 14px 24px;
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.action-btn.primary {
  background: linear-gradient(135deg, #12395b, #246794);
  color: #fff;
  box-shadow: 0 14px 30px rgba(18, 57, 91, 0.24);
}

.action-btn.secondary {
  background: #fff;
  color: #12395b;
  border: 1px solid #c8d6e2;
}

.contact-highlights {
  padding: 22px;
  display: grid;
  gap: 14px;
  background: linear-gradient(180deg, #f8fbfd, #eef4f8);
}

.highlight-card {
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid #d7e1e8;
  border-radius: 20px;
  padding: 18px;
}

.highlight-card strong {
  display: block;
  color: #12395b;
  margin-bottom: 8px;
}

.highlight-card span {
  color: #5b7084;
  line-height: 1.6;
  display: block;
}

.contact-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin: 24px 0;
}

.option-card {
  padding: 24px;
}

.option-card.featured {
  background: linear-gradient(135deg, #12395b, #184d78);
  border-color: #184d78;
}

.option-card.featured,
.option-card.featured h2,
.option-card.featured p,
.option-card.featured .eyebrow {
  color: #fff;
}

.option-card h2 {
  margin: 0 0 12px;
  color: #12304b;
  font-size: 1.5rem;
}

.option-card p {
  margin: 0 0 14px;
  color: #5a6f83;
  line-height: 1.65;
}

.inline-link {
  color: #0e5a87;
  font-weight: 700;
  text-decoration: none;
}

.option-card.featured .inline-link {
  color: #fff1b4;
}

.form-panel {
  padding: 30px;
}

.form-panel-head h2 {
  margin: 0 0 12px;
  color: #12304b;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
}

.form-panel-head p {
  margin: 0;
  color: #5b7084;
  line-height: 1.7;
  max-width: 700px;
}

.form-frame-wrap {
  margin-top: 24px;
  background: linear-gradient(180deg, #f7fafc, #eef4f8);
  border: 1px solid #d8e2ea;
  border-radius: 24px;
  padding: 14px;
}

.contact-frame {
  width: 100%;
  min-width: 0;
  height: 1080px;
  border: 0;
  border-radius: 18px;
  background: #fff;
}

.form-note {
  margin: 16px 4px 0;
  color: #63788c;
  font-size: 0.95rem;
}

.form-note a {
  color: #0e5a87;
  font-weight: 700;
  text-decoration: none;
}

@media (max-width: 900px) {
  .contact-hero,
  .contact-options {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 680px) {
  .contact-shell {
    padding: 28px 16px 56px;
  }

  .contact-highlights,
  .option-card,
  .form-panel {
    padding: 22px 18px;
    border-radius: 22px;
  }

  .contact-copy {
    padding: 0;
  }

  .hero-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .action-btn {
    width: 100%;
  }

  .form-frame-wrap {
    padding: 10px;
    border-radius: 18px;
  }

  .contact-frame {
    height: 1180px;
    border-radius: 14px;
  }
}
</style>
